import './FieldRenderer.scss';

import React, { PureComponent } from 'react';
import { List } from 'immutable';
import PropTypes from 'prop-types';

import EditableImage from 'components/EditableImage/EditableImage';
import CheckboxInput from 'components/forms/CheckboxInput';
import DayOfMonthSelectInput from 'components/forms/DayOfMonthSelectInput';
import DaySelectInput from 'components/forms/DaySelectInput';
import EnableInputSection from 'components/forms/EnableInputSection';
import FieldError from 'components/forms/FieldError';
import HourSelectInput from 'components/forms/HourSelectInput';
import Label from 'components/forms/Label';
import NumberInput from 'components/forms/NumberInput';
import RadioGroup from 'components/forms/RadioGroup';
import SelectInput from 'components/forms/SelectInput';
import TextInput from 'components/forms/TextInput';

import cxHelpers from 'util/className';

export const INPUT_TYPE_MAP = {
  text: TextInput,
  select: SelectInput,
  daySelect: DaySelectInput,
  dayOfMonthSelect: DayOfMonthSelectInput,
  hourSelect: HourSelectInput,
  number: NumberInput,
  checkbox: CheckboxInput,
  radioGroup: RadioGroup,
  enableInputSection: EnableInputSection,
  editableImage: EditableImage,
};

@cxHelpers('FieldRenderer')
export default class FieldRenderer extends PureComponent {
  static propTypes = {
    input: PropTypes.object.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    labelStyle: PropTypes.object,
    style: PropTypes.object,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    type: PropTypes.string,
    displayClassName: PropTypes.string,
    imageClassName: PropTypes.string,
    touched: PropTypes.bool,
    editing: PropTypes.bool,
    noDisplayLabel: PropTypes.bool,
    formatDisplay: PropTypes.func,
    errors: PropTypes.instanceOf(List),
    focusOnMount: PropTypes.bool,
    inputType: PropTypes.oneOf(Object.keys(INPUT_TYPE_MAP)),
    staticPosition: PropTypes.bool,
    inputOnly: PropTypes.bool,
  };

  static defaultProps = {
    inputType: 'text',
    staticPosition: false,
    labelStyle: {},
  };

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.input.focus();
    }
  }

  handleGetInputRef = input => (this.input = input);
  editableImageProps = component => {
    const { input, imageClassName, editing } = this.props;

    if (component === EditableImage) {
      return {
        src: input.value ? input.value : undefined,
        ratio: 'free',
        parentCxElClass: imageClassName,
        onFormUpdate: input.onChange,
        isEditing: editing,
        useImageComponent: true,
      };
    }

    return {};
  };

  renderValue = () => {
    const { input, formatDisplay } = this.props;

    return formatDisplay ? formatDisplay(input.value) : input.value;
  };

  render() {
    const {
      input,
      label,
      labelStyle,
      type,
      touched,
      errors,
      editing,
      displayClassName,
      formatDisplay,
      noDisplayLabel,
      inputType,
      placeholder,
      staticPosition,
      inputOnly,
      style,
      ...props
    } = this.props;

    const InputComponent = INPUT_TYPE_MAP[inputType];
    const displayCX = this.cxEl(
      'display',
      {
        show: !editing,
        'no-display-label': noDisplayLabel,
      },
      displayClassName
    );

    return (
      <div className={this.cx({ editing, [input.name]: true })} style={style}>
        {label ? (
          <Label
            htmlFor={input.name}
            className={this.cxEl('label', {
              editing,
              noDisplayLabel,
            })}
            style={labelStyle}
          >
            {label}
          </Label>
        ) : null}

        <div className={this.cxEl('value-container')}>
          <div className={this.cxEl('input-container', { show: editing })}>
            {!inputOnly && (
              <div className={displayCX}>{this.renderValue()}</div>
            )}

            <div className={this.cxEl(`${label}-input-container`)}>
              <InputComponent
                className={this.cxEl('input', {
                  show: editing,
                  static: staticPosition,
                })}
                type={type}
                placeholder={placeholder || label}
                getInputRef={this.handleGetInputRef}
                valueOnChange={InputComponent === TextInput}
                {...this.editableImageProps(InputComponent)}
                {...input}
                {...props}
              />
            </div>
          </div>
          {touched &&
            editing &&
            errors &&
            errors.map((error, i) => (
              <FieldError key={i} className={this.cxEl('error')}>
                {error}
              </FieldError>
            ))}
        </div>
      </div>
    );
  }
}
